
export type NotificationTemplateType = 'payment' | 'document' | 'attendance' | 'exam' | 'program' | 'system';

export interface NotificationTemplate {
  subject: string;
  body: string;
  channels?: string[];
  escalationNote?: string;
}

// Default templates
const DEFAULT_TEMPLATES: Record<NotificationTemplateType, NotificationTemplate> = {
  payment: {
    subject: "Payment Reminder: {{title}}",
    body: "A payment of {{amount}} is due on {{dueDate}}.",
    channels: ['push', 'email'],
    escalationNote: "This payment is overdue and requires immediate attention."
  },
  document: {
    subject: "Document Alert: {{title}}",
    body: "Your {{documentType}} will expire on {{expirationDate}}.",
    channels: ['push', 'email'],
    escalationNote: "This document is about to expire and requires immediate attention."
  },
  attendance: {
    subject: "Attendance Alert",
    body: "{{studentName}} has {{absenceCount}} absences in {{program}}.",
    channels: ['push'],
    escalationNote: "This student has exceeded the allowed absence limit."
  },
  exam: {
    subject: "Exam Results: {{examName}}",
    body: "Results for {{examName}} are now available.",
    channels: ['push', 'email']
  },
  program: {
    subject: "Program Update: {{program}}",
    body: "There has been an update to the {{program}} program.",
    channels: ['push', 'email']
  },
  system: {
    subject: "System Alert: {{title}}",
    body: "{{message}}",
    channels: ['push']
  }
};

// Get all notification templates
export const getNotificationTemplates = (): Record<NotificationTemplateType, NotificationTemplate> => {
  try {
    const savedTemplates = localStorage.getItem('notification_templates');
    if (savedTemplates) {
      return JSON.parse(savedTemplates);
    }
  } catch (error) {
    console.error('Error loading notification templates:', error);
  }
  
  return DEFAULT_TEMPLATES;
};

// Get a specific template by type
export const getNotificationTemplate = (type: NotificationTemplateType): NotificationTemplate => {
  const templates = getNotificationTemplates();
  return templates[type] || DEFAULT_TEMPLATES[type] || DEFAULT_TEMPLATES.system;
};

// Set a specific template
export const setNotificationTemplate = (type: NotificationTemplateType, template: NotificationTemplate): boolean => {
  try {
    const templates = getNotificationTemplates();
    templates[type] = template;
    localStorage.setItem('notification_templates', JSON.stringify(templates));
    return true;
  } catch (error) {
    console.error('Error saving notification template:', error);
    return false;
  }
};

// Reset all templates to defaults
export const resetNotificationTemplates = (): boolean => {
  try {
    localStorage.setItem('notification_templates', JSON.stringify(DEFAULT_TEMPLATES));
    return true;
  } catch (error) {
    console.error('Error resetting notification templates:', error);
    return false;
  }
};
